import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import {
  salesRequestStatusColors,
  salesRequestStatusLabels,
} from "@/features/sales-requests";
import { apiOutletClient, formatDate, getErrorMessage } from "@/features/shared";
import { CustomerSelect } from "@/features/customers";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { orderTypeLabels } from "@/features/purchase-orders";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const SalesRequests = () => {
  const navigate = useNavigate();
  const { outlet, changeOutlet, isHubOutlet } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [salesRequests, setSalesRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const prevOutletRoleRef = useRef();

  // Parse buyer from URL params
  const buyer = useMemo(() => {
    const buyerParam = searchParams.get("buyer");
    if (!buyerParam) return null;
    try {
      return JSON.parse(buyerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadSalesRequests = useCallback(async () => {
    if (!isHubOutlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("sales-requests", {
        params: {
          ...paginationParams,
          ...(keyword && { keyword }),
          buyerableId: buyer?.id || null,
          withBuyer: true,
          withPurchaseOrder: true,
          withCreator: true,
        },
      });
      setSalesRequests(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat permintaan penjualan"));
    } finally {
      setLoading(false);
    }
  }, [
    isHubOutlet,
    paginationParams,
    keyword,
    buyer,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadSalesRequests();
  }, [loadSalesRequests]);

  // Reset buyer when outlet role changes
  useEffect(() => {
    if (prevOutletRoleRef.current !== undefined && prevOutletRoleRef.current !== outlet?.role) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("buyer");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleBuyerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "buyer",
          JSON.stringify({ id: value.id, name: value.name })
        );
      } else {
        newParams.delete("buyer");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  return (
    <PageContainer breadcrumbs={[]} maxWidth={false}>
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <AllOutletsSelect
                value={buyer}
                onChange={handleBuyerChange}
                allowedRoles={[OutletRoleEnum.RETAIL]}
                TextFieldProps={{
                  label: "Pelanggan (Retail)",
                }}
              />
            ) : (
              <CustomerSelect
                value={buyer}
                onChange={handleBuyerChange}
                TextFieldProps={{
                  label: "Pelanggan (Customer)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Penjual",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari No. Referensi / No. PO"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={salesRequests}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "purchaseOrderReferenceNumber",
              headerName: "Nomor PO #",
              flex: 1,
              sortable: false,
              valueGetter: (value, row) => row.purchaseOrder.referenceNumber,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal Transaksi",
              flex: 1,
              sortable: false,
              valueGetter: (value, row) =>
                formatDate(row.purchaseOrder.transactionDate),
            },
            {
              field: "orderType",
              headerName: "Jenis PO",
              flex: 1,
              sortable: false,
              valueGetter: (value, row) =>
                orderTypeLabels[row.purchaseOrder.orderType],
            },
            {
              field: "creator",
              headerName: "Dibuat Oleh",
              flex: 1,
              sortable: false,
              valueGetter: (creator) => creator?.name ?? "-",
            },
            {
              field: "buyer",
              headerName: "Pelanggan",
              flex: 1.5,
              sortable: false,
              valueGetter: (value, row) => row.purchaseOrder.buyer.name,
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={salesRequestStatusLabels[row.status]}
                  color={salesRequestStatusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: (params) => {
                const actions = [];
                actions.push(
                  <IconButton
                    key="view"
                    onClick={() => navigate(params.row.referenceNumber)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                );
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
